import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import React, { useEffect, useRef, useState } from 'react';
import { Scrollbars, Tabs, Title } from '@meitu/saas-ui';
import { getImgInfoAsync } from '@meitu/saas-editor-core/es/utils/file-kit';
import { computerBgRatio } from '@meitu/saas-editor-core/es/utils/layer-kit';
import {
    IBackgroundLayerProps,
    IDynamicInfoProps,
    ILayerProps,
    ISize,
    LayerType,
    MaterialIdsType,
} from '@meitu/saas-editor-core/es/version-types';
import { CollapseType } from '@meitu/saas-editor-ui/es/types';
import { events } from '@/events-bus';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { useCollapseModel } from '@/store/collapse-models/collapse-model';
import { useElementCollapseModel } from '@/store/collapse-models/element-collapse-model';
import { useTemplateModel } from '@/store/models/template-model';
import { TabEnum } from '@/types';
import { checkImageEditor } from '@/utils/common';
import AdjustSizePanel from './components/adjust-size-panel';
import CanvasSize from './components/canvas-size';
import TabsRender from './components/tabs-render';
import './background-panel.less';
import {
    isEditAIPPTComponent as isEditAIPPTComponentFunc,
    isEditAIPPTTemplate as isEditAIPPTTemplateFunc,
    isEditorWatermark,
    isUploadWatermark,
} from '@/hooks/fetch-template/editor-mode';
import PPTMarkPanel from '../ppt-mark-panel';
import {
    IsSmartComponent,
    PptPoster,
    SmartComponentPoints,
    SmartComponentType,
} from './components/ai-ppt-components/ai-ppt-components';
import WatermarkAttribute from './components/watermark-attribute/watermark-attribute';

const isEditAIPPTComponent = isEditAIPPTComponentFunc();
const isEditAIPPTTemplate = isEditAIPPTTemplateFunc();
const isAIPPTMark = isEditAIPPTComponent || isEditAIPPTTemplate;

const { SimpleTitle, CustomTitle } = Title;

/** 打开调整尺寸面板事件 */
export const OPEN_ADJUST_SIZE_EVENT = 'OPEN_ADJUST_SIZE_EVENT';

export interface ITitleRowProps {
    leftText?: string;
    rightText?: string;
    handler?: Function;
}

const TitleNode = React.memo(({ imageEditorStyle }: { imageEditorStyle: boolean }) => {
    const { $t } = useLocaleStore();
    return (
        <div className="c-background-panel__title">
            <SimpleTitle
                text={
                    imageEditorStyle
                        ? $t((v) => v.panel.backgroundPanel.background)
                        : $t((v) => v.panel.backgroundPanel.canvas)
                }
            />
        </div>
    );
});
const TitleRow: React.FC<ITitleRowProps> = React.memo(({ leftText, rightText, handler }) => {
    const Left = <div className="title_left">{leftText}</div>;
    const Right = (
        <div
            className="title__right"
            onClick={() => {
                handler?.();
            }}
        >
            {rightText}
        </div>
    );
    return <CustomTitle leftNode={Left} rightNode={Right} />;
});

const pptMarkTabsList = [
    {
        name: '背景',
        tabKey: 'bg',
    },
    {
        name: 'ppt打标',
        tabKey: 'pptMark',
    },
];

const RenderTabs: React.FC<{
    showMaskContent: boolean;
    setShowMaskContent: (flag: boolean) => void;
    isPPT?: boolean;
}> = ({ isPPT = false, setShowMaskContent }) => {
    const [tabIndex, setTabIndex] = useState(1);
    const handleTabClick = (key: string) => {
        setTabIndex(key === 'bg' ? 1 : 2);
        setShowMaskContent(key !== 'bg');
    };

    return (
        <div style={{ padding: '0 var(--gap-lg)' }}>
            <Tabs
                showContent={false}
                data={isPPT ? pptMarkTabsList : []}
                tabsWidth="228px"
                tabsHeight="34px"
                handleTabClick={handleTabClick}
                activeIndex={tabIndex}
            />
        </div>
    );
};

const showWatermarkAttribute = isUploadWatermark() || isEditorWatermark();
const BackgroundPanel: React.FC<{}> = () => {
    const { $t } = useLocaleStore();
    const [activeStageIndex, setActiveStageIndex] = useState(editor.services.frame.getActiveIndex());
    const [layer, setLayer] = useState<ILayerProps | null>(editor.backgroundLayer.getBackgroundLayer());
    const [currentSize, setCurrentSize] = useState(editor.services.frame.getActiveFrameSize());
    const [previewStageSize, setPreviewStageSize] = useState<ISize | null>(null);
    const { width: currentWidth, height: currentHeight } = previewStageSize || currentSize;
    useEffect(() => {
        const previewHandle = editor.cropStage.onPreviewStageSizeChange(setPreviewStageSize);
        // 重绘事件早于背景写入，等待布局完成后同步图层和尺寸。
        const layoutHandle = editor.services.frame.onFrameLayoutComplete(() => {
            const stageIndex = editor.services.frame.getActiveIndex();
            setActiveStageIndex(stageIndex);
            setLayer(editor.backgroundLayer.getBackgroundLayer(stageIndex));
            setCurrentSize(editor.services.frame.getActiveFrameSize());
        });

        const handle = editor.services.frame.onActiveIndexChange((stageIndex) => {
            setActiveStageIndex(stageIndex);
            setLayer(editor.backgroundLayer.getBackgroundLayer(stageIndex));
            setCurrentSize(editor.services.frame.getActiveFrameSize());
        });

        return () => {
            editor.off(handle);
            editor.off(previewHandle);
            editor.off(layoutHandle);
        };
    }, []);

    useEffect(() => {
        const handle = editor.services.flatLayer.onChange(() => {
            setLayer(editor.backgroundLayer.getBackgroundLayer());
        }, layer?.id);

        return () => {
            editor.off(handle);
        };
    }, [layer?.id]);

    useEffect(() => {
        const handle = editor.services.stage.onResize(() => {
            setCurrentSize(editor.services.frame.getActiveFrameSize());
        });
        return () => {
            editor.off(handle);
        };
    }, []);

    const [isShowAdjust, setIsShowAdjust] = useState<boolean>(false);
    const extraInfo = useTemplateModel((state) => state.extraInfo);
    const currentCollapse = useCollapseModel((state) => state.currentType);
    const isImageEditor = checkImageEditor();
    const elementCurTab = useElementCollapseModel((state) => state.elementCurTab);
    const dynamicBgInfo = useTemplateModel((state) => state.dynamicBgInfo);
    const setDynamicBgInfo = useTemplateModel((state) => state.setDynamicBgInfo);

    const canvasType = useTemplateModel((state) => state.canvasType);
    const setCanvasType = useTemplateModel((state) => state.setCanvasType);
    const setExtraInfo = useTemplateModel((state) => state.setExtraInfo);
    const [showMaskContent, setShowMaskContent] = useState(false);
    const isBackgroundCollapse =
        currentCollapse === CollapseType.ELEMENT_MENU && elementCurTab === TabEnum.BACKGROUND_MENU;

    const [selectedIndexes, setSelectedIndexes] = useState<number[]>(editor.services.frame.getFrameSelection());

    useEffect(() => {
        const handle = editor.services.frame.onFrameSelectionChange((indexes) => {
            setSelectedIndexes(indexes);
        });
        return () => {
            editor.off(handle);
        };
    }, []);

    useEffect(() => {
        if (!layer?.id) return;
        const originLayer = editor.services.flatLayer.getLayer(layer.id, true);
        if (originLayer) {
            editor.backgroundLayer.setOriginBackground(originLayer);
        }
    }, [layer?.id]);

    useEffect(() => {
        if (!layer?.id) {
            return;
        }

        const stageAllLayers = editor.services.frame.getFrames().flat() as ILayerProps[];
        const bgLayer = stageAllLayers.find(
            (layer) =>
                layer.layerType === LayerType.BACKGROUND_LAYER && (layer.data as IBackgroundLayerProps).dynamicInfo,
        );
        if (!dynamicBgInfo && bgLayer) {
            setDynamicBgInfo({
                dynamicInfo: (bgLayer.data as IBackgroundLayerProps).dynamicInfo as IDynamicInfoProps,
                previewUrl: extraInfo?.dynamicBgPreviewUrl || '',
            });
        }
    }, [layer?.id]);

    const showAdjust = () => {
        if (!currentWidth || !currentHeight) return;
        setIsShowAdjust(true);
        editor.services.frame.setPickActiveFrame(true);
    };

    useEffect(() => {
        // 监听打开调整尺寸事件
        const openAdjustSize = () => {
            if (!currentWidth || !currentHeight) return;
            setIsShowAdjust(true);
            editor.services.frame.setPickActiveFrame(true);
        };
        events.on(OPEN_ADJUST_SIZE_EVENT, openAdjustSize);
        return () => {
            events.off(OPEN_ADJUST_SIZE_EVENT, openAdjustSize);
        };
    }, [currentWidth, currentHeight]);

    if (!layer) return null;

    const restoreOriginBackground = async () => {
        const originBackground = editor.backgroundLayer.getOriginBackground(layer.id);
        if (!originBackground) return;
        if (isEqual(originBackground, layer)) {
            return;
        }
        // 同尺寸下直接应用原来的数据
        if (originBackground.width === layer.width && originBackground.height === layer.height) {
            return editor.backgroundLayer.resetData(layer, originBackground);
        }

        const { url, dynamicInfo } = originBackground.data as IBackgroundLayerProps;
        if (dynamicInfo) {
            const newLayer = editor.backgroundLayer.generateLayer(layer, cloneDeep(originBackground.data));
            editor.backgroundLayer.resetData(newLayer, originBackground);
            return;
        }

        if (!url) {
            return editor.backgroundLayer.resetData(layer, {
                ...originBackground,
                width: layer.width,
                height: layer.height,
            });
        }
        const id = (originBackground.materialIds?.[MaterialIdsType.BACKGROUND] || [])[0];
        const { width, height } = await getImgInfoAsync(url);
        const { widthRatio, heightRatio } = computerBgRatio({
            imgWidth: width,
            imgHeight: height,
            stageHeight: layer.height,
            stageWidth: layer.width,
        });
        editor.backgroundLayer.setOriginRatio({
            url,
            backgroundColor: '',
            id: Number(id || 0),
            naturalHeight: height,
            naturalWidth: width,
            imageTransform: {
                offsetX: 0,
                offsetY: 0,
                rotate: 0,
                scale: 1,
                flipH: false,
                flipV: false,
                widthRatio,
                heightRatio,
            },
        });
    };

    return (
        <>
            {isShowAdjust ? (
                <AdjustSizePanel
                    onClose={() => {
                        setIsShowAdjust(false);
                    }}
                    activeStageIndex={activeStageIndex}
                    currentWidth={currentWidth}
                    currentHeight={currentHeight}
                />
            ) : null}
            <Scrollbars
                style={{
                    width: '100%',
                    height: '100%',
                    display: isShowAdjust ? 'none' : 'block',
                }}
            >
                {isAIPPTMark ? (
                    <div style={{ marginTop: '16px' }}>
                        <RenderTabs
                            showMaskContent={showMaskContent}
                            setShowMaskContent={setShowMaskContent}
                            isPPT={isAIPPTMark}
                        />
                    </div>
                ) : null}

                <div style={{ display: `${showMaskContent ? 'none' : 'block'}` }}>
                    <div className="c-background-panel">
                        <TitleNode imageEditorStyle={isImageEditor && isBackgroundCollapse} />

                        <IsSmartComponent extraInfo={extraInfo} setExtraInfo={setExtraInfo} />

                        <SmartComponentType extraInfo={extraInfo} setExtraInfo={setExtraInfo} />

                        <SmartComponentPoints extraInfo={extraInfo} />

                        <PptPoster canvasType={canvasType} setCanvasType={setCanvasType} />

                        {selectedIndexes.length <= 1 ? (
                            <>
                                {isImageEditor ? (
                                    <TitleRow
                                        leftText={
                                            isBackgroundCollapse
                                                ? $t((v) => v.panel.backgroundPanel.backgroundSize)
                                                : $t((v) => v.panel.backgroundPanel.imageSize)
                                        }
                                        rightText=""
                                    />
                                ) : (
                                    <TitleRow
                                        leftText={$t((v) => v.panel.backgroundPanel.canvasSize)}
                                        rightText={$t((v) => v.panel.backgroundPanel.adjustSize)}
                                        handler={showAdjust}
                                    />
                                )}
                                <CanvasSize
                                    imageEditorStyle={isImageEditor}
                                    handler={showAdjust}
                                    currentWidth={currentWidth}
                                    currentHeight={currentHeight}
                                />
                            </>
                        ) : null}

                        {(isImageEditor && isBackgroundCollapse) || (!isImageEditor && layer && layer?.id) ? (
                            <>
                                <TitleRow
                                    leftText={$t((v) => v.panel.backgroundPanel.canvasBackground)}
                                    rightText={$t((v) => v.panel.backgroundPanel.restoreBackground)}
                                    handler={async () => {
                                        restoreOriginBackground();
                                    }}
                                />
                                <TabsRender
                                    selectedIndexes={selectedIndexes}
                                    layer={layer}
                                    currentWidth={currentWidth}
                                    currentHeight={currentHeight}
                                />
                            </>
                        ) : null}

                        {showWatermarkAttribute ? <WatermarkAttribute /> : null}
                    </div>
                </div>

                {isAIPPTMark && showMaskContent ? <PPTMarkPanel editor={editor} /> : null}
            </Scrollbars>
        </>
    );
};

export default React.memo(BackgroundPanel);
